import { useDirection } from '@radix-ui/react-direction'
import { lazy, Suspense } from 'react'
import { Navigate, Outlet, Route, Routes } from 'react-router-dom'

import { useCurrentUser } from './api/auth/queries/useCurrentUser'
import { useUserPickupOrders } from './api/pickup-orders/queries/useUserPickupOrders'
import { useUserPkgs } from './api/user-package/queries/useUserPkgs'
import { Spinner } from './components/ui/spinner'
import ScrollToTop from './helpers/ScrollToTop'
import { usePageTitle } from './hooks/usePageTitle'
import ClientPage from './pages/client-side/ClientPage'
import ClientPkgPage from './pages/client-side/ClientPkg/ClientPkgsPage'
import ForgotPasswordPage from './pages/client-side/Login/ForgotPasswordPage'
import LoginPage from './pages/client-side/Login/LoginPage'
import MyAddressesPage from './pages/client-side/MyAddresses/MyAddresses'
import PaymentPage from './pages/client-side/Payment/PaymentPage'
import ProfilePage from './pages/client-side/Profile/ProfilePage'
import ReceiptUploadPageV2 from './pages/client-side/ReceiptUpload/ReceiptUploadPage'
import ReturnPkgPage from './pages/client-side/ReturnPkg/ReturnPkgPage'
import UnitePkgPage from './pages/client-side/UnitePkg/UnitePkgPage'
import { RoutePath } from './RoutePath'
const AddNewPackagePage = lazy(() => import('./pages/office-side/AddNewPackage/AddNewPackagePage'))
const AffiliatePage = lazy(() => import('./pages/office-side/Affiliate/AffiliatePage'))
const BarcodeGunShip = lazy(() => import('./pages/office-side/BarcodeGunShip/BarcodeGunShip'))
const CouponOfficePage = lazy(() => import('./pages/office-side/Coupon/CouponOfficePage'))
const DelayedPayments = lazy(() => import('./pages/office-side/DelayedPayments/DelayedPayments'))
const EditAndUpdatePage = lazy(() => import('./pages/office-side/EditAndUpdate/EditAndUpdatePage'))
const Location = lazy(() => import('./pages/office-side/Location/Location'))
const ScanMe = lazy(() => import('./pages/office-side/ScanMe/ScanMe'))
const PkgShippingChecklist = lazy(() => import('./pages/office-side/PackageChecklist/PkgShippingChecklist'))
const PackageRequests = lazy(() => import('./pages/office-side/PackageRequests/PackageRequests'))
const PopularBrandsItems = lazy(() => import('./pages/office-side/PopularBrandsItems/PopularBrandsItems'))
const PrintPkgPage = lazy(() => import('./pages/office-side/Print/PrintPkgPage'))
const ReceiveShipments = lazy(() => import('./pages/office-side/RecieveShipments/ReceiveShipments'))
const OfficeShipPageComponent = lazy(() => import('./pages/office-side/Ship/OfficeShipPageComponent'))
const Stats = lazy(() => import('./pages/office-side/Stats/StatsPage'))
const Sticker = lazy(() => import('./pages/office-side/Sticker/Sticker'))
const WarehouseArrivalsPage = lazy(() => import('./pages/office-side/WarehouseArrivals/WarehouseArrivalsPage'))
const PickupOrdersPage = lazy(() => import('./pages/office-side/PickupOrders/PickupOrdersPage'))
const ScanPickupOrdersPage = lazy(() => import('./pages/office-side/PickupOrders/ScanPickupOrdersPage'))
const USAPackageLookupPage = lazy(() => import('./pages/office-side/USAPackageLookup/USAPackageLookupPage'))
const USAPkgLookupsListPage = lazy(() => import('./pages/office-side/USAPkgLookupsList/USAPkgLookupsListPage'))

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const RouteWrapper = ({ Component, ...props }: { Component: React.LazyExoticComponent<React.ComponentType<any>> }) => {
  return (
    <Suspense
      fallback={
        <div className='flex items-center justify-center' style={{ height: '100dvh' }}>
          <Spinner className='size-12' />
        </div>
      }
    >
      <Component {...props} />
    </Suspense>
  )
}

const ProtectedRoute = ({ children }: { children: React.ReactNode }) => {
  const { data: loggedUser } = useCurrentUser()
  return loggedUser?.Username ? <>{children}</> : <LoginPage />
}

// Wrapper for client routes with ClientPage layout
const ClientLayout = () => {
  const { data: loggedUser } = useCurrentUser()
  useUserPkgs()
  useUserPickupOrders()
  if (!loggedUser?.Username) return <LoginPage />
  return (
    <ClientPage>
      <Outlet />
    </ClientPage>
  )
}

export default function Layout() {
  const { data: loggedUser } = useCurrentUser()
  const direction = useDirection()
  usePageTitle()
  return (
    <div dir={direction} className='h-[calc(100dvh-var(--navbar-height))]'>
      <ScrollToTop>
        <Routes>
          <Route path='/welcome' element={<LoginPage />} />

          <Route path={RoutePath.Login} element={<LoginPage />} />

          <Route path={RoutePath.ForgotPassword} element={<ForgotPasswordPage />} />

          {/* Client routes with shared layout */}
          <Route path={RoutePath.Client} element={<ClientLayout />}>
            <Route path={RoutePath.ClientProfile} element={<ProfilePage />} />
            <Route path={RoutePath.ClientProfilePersonal} element={<ProfilePage />} />
            <Route path={RoutePath.ClientProfileAddresses} element={<ProfilePage />} />
            <Route path={RoutePath.ClientProfileAffiliate} element={<ProfilePage />} />
            <Route path={RoutePath.ClientAddresses} element={<MyAddressesPage />} />
            <Route path={`${RoutePath.ClientPkgReturn}/:pkgId`} element={<ReturnPkgPage />} />
            <Route path={RoutePath.ClientPkg} element={<ClientPkgPage />} />
            <Route path={RoutePath.ClientPkgUnite} element={<UnitePkgPage />} />
          </Route>

          <Route
            path={`${RoutePath.ReceiptUpload}/:pkgId`}
            element={
              <ProtectedRoute>
                <ReceiptUploadPageV2 />
              </ProtectedRoute>
            }
          />

          <Route
            path={`payment/:step/:pkgId`}
            element={
              <ProtectedRoute>
                <PaymentPage />
              </ProtectedRoute>
            }
          />

          <Route
            path='/office'
            element={
              <ProtectedRoute>
                {loggedUser &&
                  (loggedUser.permissions['Admin'] || loggedUser.permissions['Worker'] || loggedUser.permissions['OfficeManager']) && (
                    <RouteWrapper Component={AddNewPackagePage} />
                  )}
              </ProtectedRoute>
            }
          />

          <Route
            path='/office-ship'
            element={
              <ProtectedRoute>
                {loggedUser &&
                  (loggedUser.permissions['Admin'] || loggedUser.permissions['Worker'] || loggedUser.permissions['OfficeManager']) && (
                    <RouteWrapper Component={OfficeShipPageComponent} />
                  )}
              </ProtectedRoute>
            }
          />

          <Route
            path='/office-e&u/*'
            element={
              <ProtectedRoute>
                {loggedUser &&
                  (loggedUser.permissions['Admin'] ||
                    loggedUser.permissions['Agent'] ||
                    loggedUser.permissions['OfficeManager'] ||
                    loggedUser.permissions['SeniorAgent'] ||
                    loggedUser.permissions['Worker']) && <RouteWrapper Component={EditAndUpdatePage} />}
              </ProtectedRoute>
            }
          />

          <Route
            path='/office-packageRequests/:reqType?/:reqId?'
            element={
              <ProtectedRoute>
                {loggedUser &&
                  (loggedUser.permissions['Admin'] ||
                    loggedUser.permissions['SeniorAgent'] ||
                    loggedUser.permissions['Agent'] ||
                    loggedUser.permissions['Worker'] ||
                    loggedUser.permissions['OfficeManager']) && <RouteWrapper Component={PackageRequests} />}
              </ProtectedRoute>
            }
          />

          <Route
            path='/office-print'
            element={
              <ProtectedRoute>
                {loggedUser &&
                  (loggedUser.permissions['Admin'] ||
                    loggedUser.permissions['Agent'] ||
                    loggedUser.permissions['SeniorAgent'] ||
                    loggedUser.permissions['Worker'] ||
                    loggedUser.permissions['OfficeManager']) && <RouteWrapper Component={PrintPkgPage} />}
              </ProtectedRoute>
            }
          />

          <Route
            path='/office-checkForm'
            element={
              <ProtectedRoute>
                {loggedUser &&
                  (loggedUser.permissions['Admin'] || loggedUser.permissions['Worker'] || loggedUser.permissions['OfficeManager']) && (
                    <RouteWrapper Component={PkgShippingChecklist} />
                  )}
              </ProtectedRoute>
            }
          />

          <Route
            path='/office-stats'
            element={
              <ProtectedRoute>
                {loggedUser && (loggedUser.permissions['Admin'] || loggedUser.permissions['OfficeManager']) && (
                  <RouteWrapper Component={Stats} />
                )}
              </ProtectedRoute>
            }
          />

          <Route
            path='/office-popular-brands-items'
            element={
              <ProtectedRoute>
                {loggedUser && loggedUser.permissions['Admin'] && <RouteWrapper Component={PopularBrandsItems} />}
              </ProtectedRoute>
            }
          />

          <Route
            path='/office-affiliate'
            element={
              <ProtectedRoute>{loggedUser && loggedUser.permissions['Admin'] && <RouteWrapper Component={AffiliatePage} />}</ProtectedRoute>
            }
          />

          <Route
            path='/office-coupon'
            element={
              <ProtectedRoute>
                {loggedUser && loggedUser.permissions['Admin'] && <RouteWrapper Component={CouponOfficePage} />}
              </ProtectedRoute>
            }
          />

          <Route
            path='/office-receive-shipments'
            element={
              <ProtectedRoute>
                {loggedUser && (loggedUser.permissions['Admin'] || loggedUser.permissions['Worker']) && (
                  <RouteWrapper Component={ReceiveShipments} />
                )}
              </ProtectedRoute>
            }
          />

          <Route
            path='/office-delayed-payments'
            element={
              <ProtectedRoute>
                {loggedUser &&
                  (loggedUser.permissions['Admin'] || loggedUser.permissions['Worker'] || loggedUser.permissions['OfficeManager']) && (
                    <RouteWrapper Component={DelayedPayments} />
                  )}
              </ProtectedRoute>
            }
          />

          <Route
            path='/office-ship-barcode-gun'
            element={
              <ProtectedRoute>
                {loggedUser &&
                  (loggedUser.permissions['Admin'] || loggedUser.permissions['OfficeManager'] || loggedUser.permissions['Worker']) && (
                    <RouteWrapper Component={BarcodeGunShip} />
                  )}
              </ProtectedRoute>
            }
          />

          <Route
            path='/office-location'
            element={
              <ProtectedRoute>
                {loggedUser &&
                  (loggedUser.permissions['Admin'] || loggedUser.permissions['OfficeManager'] || loggedUser.permissions['Worker']) && (
                    <RouteWrapper Component={Location} />
                  )}
              </ProtectedRoute>
            }
          />

          <Route
            path='/office-scan-me'
            element={
              <ProtectedRoute>
                {loggedUser &&
                  (loggedUser.permissions['Admin'] || loggedUser.permissions['OfficeManager'] || loggedUser.permissions['Worker']) && (
                    <RouteWrapper Component={ScanMe} />
                  )}
              </ProtectedRoute>
            }
          />

          <Route
            path='/office-warehouse-arrivals'
            element={
              <ProtectedRoute>
                {loggedUser && (loggedUser.permissions['Admin'] || loggedUser.permissions['OfficeManager']) && (
                  <RouteWrapper Component={WarehouseArrivalsPage} />
                )}
              </ProtectedRoute>
            }
          />

          <Route
            path='/office-usa-package-lookup'
            element={
              <ProtectedRoute>
                {loggedUser &&
                  (loggedUser.permissions['Admin'] ||
                    loggedUser.permissions['Agent'] ||
                    loggedUser.permissions['SeniorAgent'] ||
                    loggedUser.permissions['OfficeManager'] ||
                    loggedUser.permissions['Worker']) && <RouteWrapper Component={USAPackageLookupPage} />}
              </ProtectedRoute>
            }
          />

          <Route
            path='/office-usa-pkg-lookups'
            element={
              <ProtectedRoute>
                {loggedUser && (loggedUser.permissions['Admin'] || loggedUser?.UserAttributes?.['custom:warehouse'] === 'USA') && (
                  <RouteWrapper Component={USAPkgLookupsListPage} />
                )}
              </ProtectedRoute>
            }
          />

          <Route
            path='/office-pickup-orders'
            element={
              <ProtectedRoute>
                {loggedUser &&
                  (loggedUser.permissions['Admin'] ||
                    loggedUser.permissions['Agent'] ||
                    loggedUser.permissions['SeniorAgent'] ||
                    loggedUser.permissions['OfficeManager'] ||
                    loggedUser.permissions['Worker']) && <RouteWrapper Component={PickupOrdersPage} />}
              </ProtectedRoute>
            }
          />

          <Route
            path='/office-scan-orders'
            element={
              <ProtectedRoute>
                {loggedUser &&
                  (loggedUser.permissions['Admin'] ||
                    loggedUser.permissions['Agent'] ||
                    loggedUser.permissions['SeniorAgent'] ||
                    loggedUser.permissions['OfficeManager'] ||
                    loggedUser.permissions['Worker'] ||
                    loggedUser.permissions['PickupOrderDriver']) && <RouteWrapper Component={ScanPickupOrdersPage} />}
              </ProtectedRoute>
            }
          />

          <Route
            path='/office-sticker/:pId/:name/:unit/:brand'
            element={
              <ProtectedRoute>
                {loggedUser &&
                  (loggedUser.permissions['Admin'] || loggedUser.permissions['OfficeManager'] || loggedUser.permissions['Worker']) && (
                    <RouteWrapper Component={Sticker} />
                  )}
              </ProtectedRoute>
            }
          />

          <Route path='/' element={<LoginPage />} />
          <Route path='/personal-info' element={<Navigate to={RoutePath.ClientAddresses} replace />} />
          <Route path='/receipt-upload' element={<Navigate to={RoutePath.ClientAddresses} replace />} />
          <Route path='/package-details' element={<Navigate to={RoutePath.ClientAddresses} replace />} />
          <Route path='/package-return-label' element={<Navigate to={RoutePath.ClientAddresses} replace />} />
          <Route path='/payment' element={<Navigate to={RoutePath.ClientAddresses} replace />} />
          <Route path='/payment-successful' element={<Navigate to={RoutePath.ClientAddresses} replace />} />
          <Route path='/rp-payment-successful' element={<Navigate to={RoutePath.ClientAddresses} replace />} />
          <Route path='/return-package' element={<Navigate to={RoutePath.ClientAddresses} replace />} />
        </Routes>
      </ScrollToTop>
    </div>
  )
}
